import React, { useEffect, useMemo, useState } from "react";

import { getClientsInGroup } from "../../api";
import { BillingByYearTable, IncompleteDataBanner } from "../../components/rollup";
import useClientRollup from "../../hooks/useClientRollup";
import { byYear, feesBilledOf, wipMetrics } from "../../utils/rollup";

// Unbilled work, in view while you decide the fee.
//
// Every figure here comes from rollup.js/useClientRollup -- the same pair
// behind ClientDetailPage and ClientGroupPage -- so a client's WIP is one
// number wherever it is read. Nothing in this file computes it.
//
// The headline is "to bill": the WIP left, all years. Underneath it the
// MasterFiles table (BillingByYearTable) breaks the same money down by fiscal
// year -- time value, fees billed, discount, write-offs, WIP left -- and each
// year expands to the time behind it and the invoices already raised against
// it. The table's total WIP is the headline: both are byYear()'s rows.
// It sits right under the client picker, so the figure is in view before the
// fee is typed (owner, 2026-10-26: "I should easily tell how much I need to
// bill the client").
//
// Read-only on purpose: nothing in the schema records which time an invoice
// line came from, so a click-to-add-line control could bill the same hours
// twice in one sitting with nothing able to notice. That same gap is why there
// is no breakdown by task: TimeEntry has a task FK but Invoice and WriteOff do
// not, so a per-task figure could only ever be gross time, never WIP. See the
// spec.

const money = (n) => {
  const v = Number(n) || 0;
  const body = Math.abs(v).toLocaleString("en-CA", {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });
  return v < 0 ? `-$${body}` : `$${body}`;
};

const CELL = { padding: "4px 8px", borderBottom: "1px solid #F3F4F6", whiteSpace: "nowrap" };
const NOTE = { fontSize: 12, color: "#6B7280", padding: "3px 8px" };

// The only arithmetic this file asks for, and it is still rollup.js's: the
// invoice being edited must not count against its own WIP -- not in the
// headline, and not in the year it belongs to either. The year rows are
// recomputed here rather than taken from the hook because the hook's rows know
// nothing about excludeInvoiceId.
function viewOf(rollup, excludeInvoiceId) {
  const { timeEntries, writeOffs, clientsById } = rollup;
  const invoices = excludeInvoiceId
    ? (rollup.invoices || []).filter((i) => String(i.id) !== String(excludeInvoiceId))
    : (rollup.invoices || []);
  const metrics = excludeInvoiceId
    ? wipMetrics({ timeEntries, invoices, writeOffs, clientsById })
    : rollup.metrics;
  const yearRows = byYear({ timeEntries, invoices, writeOffs, clientsById });
  return { metrics, yearRows };
}

// The records behind one fiscal year, under its row when it is opened.
function YearDetail({ row }) {
  return (
    <div data-testid={`wip-detail-${row.year === null ? "__unfiled__" : row.year}`}>
      {row.timeEntries.length ? (
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
          <tbody>
            {row.timeEntries.map((t) => (
              <tr key={t.id}>
                <td style={CELL}>{t.date}</td>
                <td style={CELL}>{t.user_name || ""}</td>
                <td style={{ ...CELL, whiteSpace: "normal" }}>{t.task_name || ""}</td>
                <td style={{ ...CELL, textAlign: "right" }}>{`${t.hours}h`}</td>
                <td style={{ ...CELL, textAlign: "right" }}>{money(t.billable_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {row.invoices.map((i) => (
        <div key={`invoice-${i.id}`} style={NOTE}>
          {`Billed: ${i.number || "—"} · ${i.date || ""} — fees ${money(feesBilledOf(i))}`
            + (Number(i.discount) ? `, discount ${money(i.discount)}` : "")}
        </div>
      ))}
      {row.writeOffEntries.map((w) => (
        <div key={`writeoff-${w.id}`} style={NOTE}>
          {`Written off: ${w.date || ""} — ${money(w.amount)}`}
        </div>
      ))}
    </div>
  );
}

export default function WipPanel({ clientId, groupId, excludeInvoiceId }) {
  const [tab, setTab] = useState("client");
  const [groupIds, setGroupIds] = useState([]);
  const [groupFetchFailed, setGroupFetchFailed] = useState(false);
  // Membership is unknown until this settles. An unknown membership makes the
  // group roll-up an empty id list, which the hook reports as loaded with
  // nothing in it -- indistinguishable from a group that owes nothing.
  const [groupMembersLoading, setGroupMembersLoading] = useState(Boolean(groupId));

  useEffect(() => {
    let cancelled = false;
    if (!groupId) {
      setGroupIds([]);
      setGroupFetchFailed(false);
      setGroupMembersLoading(false);
      return undefined;
    }
    setGroupMembersLoading(true);
    getClientsInGroup(groupId)
      .then((members) => {
        if (!cancelled) {
          setGroupIds(members.map((c) => c.id));
          setGroupFetchFailed(false);
          setGroupMembersLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGroupIds([]);
          setGroupFetchFailed(true);
          setGroupMembersLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  // Both roll-ups load unconditionally: hooks cannot be called conditionally,
  // and the hook already treats an empty id list as "nothing to load".
  const clientRollup = useClientRollup(useMemo(() => (clientId ? [clientId] : []), [clientId]));
  const groupRollup = useClientRollup(groupIds);

  const clientView = viewOf(clientRollup, excludeInvoiceId);
  const groupView = viewOf(groupRollup, excludeInvoiceId);

  const groupTabActive = Boolean(tab === "group" && groupId);
  const active = groupTabActive ? groupRollup : clientRollup;
  const view = groupTabActive ? groupView : clientView;
  // Nothing here is known yet. Same rule as a failed fetch, one step earlier:
  // never present a figure the panel cannot stand behind. A confident
  // "$0 unbilled / No unbilled work recorded" fires on EVERY client selection
  // and is read by someone pricing a job seconds before the real number lands.
  const isLoading = groupTabActive
    ? (groupMembersLoading || groupRollup.loading)
    : clientRollup.loading;
  // If the group tab is active but the fetch failed, treat as incomplete.
  const isGroupTabActiveAndFailed = groupTabActive && groupFetchFailed;
  const isIncomplete = !isLoading && (active.partial || isGroupTabActiveAndFailed);

  const metrics = view.metrics || { wip: 0 };
  const yearRows = isLoading ? [] : view.yearRows;

  const tabStyle = (key) => ({
    padding: "4px 10px",
    background: "transparent",
    border: "none",
    borderBottom: `2px solid ${tab === key ? "#111827" : "transparent"}`,
    fontWeight: tab === key ? 600 : 400,
    cursor: "pointer",
  });

  const groupWipFigure = () => {
    if (groupFetchFailed) {
      return "—";
    }
    // Not known yet is not zero. Distinct from "—" (known to be unknown) so
    // the two states do not read as the same thing.
    if (groupMembersLoading || groupRollup.loading) {
      return "…";
    }
    return money((groupView.metrics || {}).wip);
  };

  return (
    <div style={{ border: "1px solid #E5E7EB", borderRadius: 6, padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <strong>WIP</strong>
        <div role="tablist" style={{ display: "flex", gap: 4 }}>
          <button type="button" role="tab" aria-selected={tab === "client"}
                  onClick={() => setTab("client")} style={tabStyle("client")}>
            Client
          </button>
          {groupId ? (
            <button type="button" role="tab" aria-selected={tab === "group"}
                    onClick={() => setTab("group")} style={tabStyle("group")}>
              {`Group · ${groupWipFigure()}`}
            </button>
          ) : null}
        </div>
      </div>

      {isIncomplete ? (
        <>
          <div style={{ color: "#B45309", fontSize: 12, marginTop: 6 }}>
            Incomplete — some data could not be loaded, so this figure is a floor.
          </div>
          {active.partial ? (
            <IncompleteDataBanner
              failedClientIds={active.failedClientIds}
              failedFetches={active.failedFetches}
            />
          ) : null}
        </>
      ) : null}

      <div data-testid="wip-total" style={{ fontSize: 22, fontWeight: 700, marginTop: 6, color: "#b45309" }}>
        {isLoading
          ? "To bill — loading…"
          : isIncomplete
            ? `To bill: at least ${money(metrics.wip)}`
            : `To bill: ${money(metrics.wip)}`}
      </div>
      <div data-testid="wip-working" style={{ color: "#6B7280", fontSize: 12 }}>
        {isLoading
          ? " "
          : "WIP left = time − fees billed − write-offs. Discounts clear WIP; drafts don't count until issued."}
      </div>

      {isLoading ? null : yearRows.length === 0 ? (
        <p style={{ color: "#6B7280", fontSize: 13 }}>
          No time or invoices recorded for this client.
        </p>
      ) : (
        <div style={{ maxHeight: 320, overflowY: "auto", marginTop: 8 }}>
          <BillingByYearTable
            rows={yearRows}
            short
            allowToggle
            fontSize={13}
            renderDetail={(row) => <YearDetail row={row} />}
          />
        </div>
      )}
    </div>
  );
}
